import React, { Component, Fragment } from 'react';
import { InfoCircleFilled } from '@ant-design/icons';
import { Text, mergeClassNames } from '@clockout-dev/design-elements';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import Page from '../common/page/Page';
import SplitLayout from '../common/layouts/splitLayout/SplitLayout';
import { SplitLayoutSide, SplitLayoutVariant } from '../common/layouts/splitLayout/splitLayout.constants';
import { SCOPES } from '../../constants/scopes.constants';
import TicketController from './ticket.controller';
import TicketThread from './elements/TicketThread';
import TicketDetailsCard from './elements/TicketDetailsCard';
import TicketLinkedEventCard from './elements/TicketLinkedEventCard';
import TicketJiraLink from './elements/TicketJiraLink';
import IssueDetailModal from './elements/IssueDetailModal';
import { SupportTicketConstants } from './support.enums';
import styles from './styles/ticket.module.scss';
import TicketMessageInput from './elements/TicketMessageInput';

class Ticket extends Inherit(TicketController, Component) {
    render() {
        const {
            ticket, ticketId, ticketLoading, replyBody, replyAttachments, replySending, messagesLoadingMore,
            issueDetailKey, issueDetail, issueDetailLoading, issueDetailError,
        } = this.state;

        const status = ticket?.status;
        const isClosedThread =
            status === SupportTicketConstants.STATUS.RESOLVED || status === SupportTicketConstants.STATUS.CLOSED;

        return (
            <Page
                isLoading={Boolean(ticketId && ticketLoading)}
                scope={SCOPES.SUPPORT_VIEW}
                notificationContextHolder={this.props.notificationContextHolder}
                goBack={{ label: 'Back to support', onClick: this.navigateSupport.bind(this) }}
                title={ticket ? { text: 'Ticket:', highlight: ticket.subject } : undefined}
            >
                <div className={styles.container}>
                    {!ticketId ? (
                        <Text className={styles.missing} size="l">
                            Missing ticket. Open a ticket from the support list.
                        </Text>
                    ) : null}
                    {ticketId && !ticketLoading && !ticket ? (
                        <Text className={styles.notFound} size="l">
                            Ticket not found or you do not have access.
                        </Text>
                    ) : null}
                    {ticketId && !ticketLoading && ticket ? (
                        <Fragment>
                            <SplitLayout
                                variant={SplitLayoutVariant.TWO_THIRDS}
                                embedded
                                sideVariant={SplitLayoutSide.RIGHT}
                                className={styles.splitRoot}
                                contentClassName={styles.splitContent}
                                sideClassName={styles.splitSide}
                                side={
                                    <Fragment>
                                        <TicketDetailsCard ticket={ticket} />
                                        <TicketLinkedEventCard
                                            linkedEvent={ticket?.linkedEvent}
                                            canAccessReviews={this.hasScope(SCOPES.REVIEWS_VIEW)}
                                            onGoToEvent={() => this.navigateToReviewDetail(ticket?.linkedEvent?.eventId)}
                                        />
                                        {/* Same scope as the Jira tab, so a role
                                            that cannot browse the board cannot
                                            read the board through a ticket
                                            either. The server enforces it too:
                                            without admin:jira:tickets:view the
                                            ticket-detail response carries no
                                            `jiraLinks` key at all, so this is
                                            the second of two gates, not the
                                            only one. `TicketJiraLink` renders
                                            null for a missing array anyway. */}
                                        {this.canViewJiraTickets() ? (
                                            <TicketJiraLink
                                                jiraLinks={ticket?.jiraLinks}
                                                onOpenIssue={(key) => this.openIssueDetail(key)}
                                                resolveStatusEntry={(key) => this.servedStatusEntry(key)}
                                            />
                                        ) : null}
                                    </Fragment>
                                }
                            >
                                <div className={styles.conversationStack}>
                                    <div className={styles.conversationCard}>
                                        <div className={styles.conversationPanel}>
                                            <TicketThread
                                                threadKey={ticketId}
                                                messages={ticket.messages || []}
                                                hasMore={Boolean(ticket.messagesPageInfo?.hasNext)}
                                                loadingMore={messagesLoadingMore}
                                                onLoadMore={() => this.loadMoreTicketMessages()}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className={mergeClassNames([
                                            styles.replySection,
                                            isClosedThread ? styles.replySectionClosed : null,
                                        ])}
                                    >
                                        {isClosedThread ? (
                                            <div className={styles.closedBanner}>
                                                <InfoCircleFilled className={styles.closedBannerIcon} />
                                                <div className={styles.closedBannerContent}>
                                                    <Text className={styles.closedBannerTitle}>
                                                        This ticket is resolved or closed
                                                    </Text>
                                                    <Text className={styles.closedBannerBody}>
                                                        Open a new ticket from Support if you need more help.
                                                    </Text>
                                                </div>
                                            </div>
                                        ) : (
                                            <TicketMessageInput
                                                replyBody={replyBody}
                                                replyAttachments={replyAttachments}
                                                replySending={replySending}
                                                onReplyBodyChange={(value) => this.setReplyBody(value)}
                                                onReplyAttachmentsChange={(value) => this.setReplyAttachments(value)}
                                                onSendReply={this.handleSendReply.bind(this)}
                                            />
                                        )}
                                    </div>
                                </div>
                            </SplitLayout>

                            {/* The same modal the Jira tab opens, so an issue
                                reads identically from either side of the link.
                                A support ticket listed inside it navigates
                                there — including back to this one. */}
                            <IssueDetailModal
                                issueKey={issueDetailKey}
                                issue={issueDetail}
                                loading={issueDetailLoading}
                                error={issueDetailError}
                                onClose={() => this.closeIssueDetail()}
                                onOpenTicket={(id) => this.navigateTicket(id)}
                                resolveStatusEntry={(key) => this.servedStatusEntry(key)}
                                resolveStageLabel={(stage) => this.servedStageLabel(stage)}
                            />
                        </Fragment>
                    ) : null}
                </div>
            </Page>
        );
    }
}

export default withHooks(Ticket);
